import React, { useMemo } from "react";

import { Runtime } from "@morphism/fp";
import { ReadonlyArray, pipe } from "fp";
import * as GenericFilter from "~/Filter";
import { Order } from "~/Order";
import * as Driver from "~/Driver";
import { Remote } from "~/Remote";
import { Request } from "~/Request";

import { noBranchDisplay, NoBranchDisplay, NewBranch } from "~/Branch";

import { ID } from "~/ID";

import { Company } from "~/Company";

import * as Preferences from "~/Preferences";

export namespace Branch {
  export const toString = ({ branch: { name } }: Branches.Response.Branch) => {
    return name ?? noBranchDisplay();
  };

  export type NoBranchID = "no-branch";
  export const noBranchID = (): NoBranchID => "no-branch";

  type NoBranch = {
    readonly branch_id: NoBranchID;
    readonly name: NoBranchDisplay;
  };

  export namespace Branches {
    export const name = (branch: Response.Branch | undefined) =>
      branch?.branch.name ?? noBranchDisplay();

    export const order = () =>
      Order.fromCompare<Response.Branch>((a, b) => {
        return Order.string().compare(
          toString(a).toLowerCase(),
          toString(b).toLowerCase()
        );
      });

    export namespace Response {
      export const runtime = () => Runtime.array(NewBranch.Response.runtime());

      export const decode = (
        encoded: Runtime.ToType<typeof runtime>
      ): Branches => ({
        branches: pipe(
          encoded,
          ReadonlyArray.map((branch) => ({
            branch: NewBranch.Response.decode(branch),
          }))
        ),
      });

      export type Branch = {
        readonly branch: NewBranch.Branch | NoBranch;
      };

      export type Branches = { readonly branches: readonly Branch[] };

      export const request = ({
        company,
      }: {
        readonly company: ID;
      }): Request.Fn<Branches> => {
        return flow(
          Request.fromConfig({
            endpoint: `/companies/${company.id}/branches?active=true`,
            headers: { "X-Fields": "{branch_id name}" },
          }),
          Request.Expect.status(200),
          Request.Expect.json(Response.runtime()),
          Request.onSuccess(Response.decode)
        );
      };

      export const use = (): Remote<Branches> => {
        const { company } = Company.use();
        return Remote.Query.use(["Branches", company.id], request({ company }));
      };
    }

    export type BranchIDs = Driver.Filter.BranchFilter;

    export namespace Filter {
      type Values = {
        readonly branchIDs: readonly number[];
        readonly includeNoBranch: boolean;
      };
      export const parseValues = (branchIDs: BranchIDs): Values => {
        const noBranchID = Branch.noBranchID();
        const includeNoBranch = branchIDs.indexOf(noBranchID) > -1;
        const ids = branchIDs.filter(
          (id) => typeof id === "number"
        ) as readonly number[];

        return {
          branchIDs: ids,
          includeNoBranch: includeNoBranch,
        };
      };

      type Props = {
        readonly onFilter: (branchIds: BranchIDs) => void;
        readonly label?: string;
      };

      export const Component = ({
        onFilter,
        label = "Branch",
      }: Props): JSX.Element => {
        const processedBranches = useBranchesAsFilterOptions();

        const onClose = (
          allBranches: readonly GenericFilter.FilterOption[]
        ) => {
          const selectedBranches = allBranches
            .filter(({ checked }) => checked === "on")
            .map(({ data }) =>
              data.id === Branch.noBranchID() ? data.id : Number(data.id)
            );

          onFilter && onFilter(selectedBranches);
        };

        return (
          <GenericFilter.Filter
            isLoading={false}
            onClose={onClose}
            label={label}
            initialItems={processedBranches}
          ></GenericFilter.Filter>
        );
      };
    }

    export const useBranchFiltersFromPreferences = (): readonly [
      boolean,
      BranchIDs
    ] => {
      const [loading, driverBranches] = Remote.when(
        Preferences.Get.use<BranchIDs>(),
        {
          loading: () => [true, []],
          error: () => [false, []],
          success: (preferences) => {
            return [
              false,
              preferences[Preferences.preferenceKey("branchFilter")],
            ];
          },
        }
      );
      return [loading, driverBranches ?? []];
    };

    // eslint-disable-next-line functional/prefer-readonly-type
    export const useBranchesAsFilterOptions = (): GenericFilter.FilterOption[] => {
      const [
        preferencesLoading,
        preferences,
      ] = useBranchFiltersFromPreferences();

      const [branchesLoading, branches] = Remote.when(
        Company.Branch.Branches.Response.use(),
        {
          loading: () => [true, []],
          error: () => [false, []],
          success: (branchesResponse) => {
            return [
              false,
              pipe(
                branchesResponse.branches,
                ReadonlyArray.sort(Company.Branch.Branches.order())
              ),
            ];
          },
        }
      );

      const formatBranch = (
        branch: Branches.Response.Branch
      ): GenericFilter.FilterOption => {
        return {
          label: Branch.toString(branch),
          checked:
            preferences && preferences.includes(branch.branch.branch_id)
              ? "on"
              : undefined,
          data: { id: String(branch.branch.branch_id) },
        };
      };

      const noBranchBranch: Response.Branch = {
        branch: {
          branch_id: Branch.noBranchID(),
          name: noBranchDisplay(),
        },
      };

      const allOptions = [noBranchBranch, ...branches];
      return useMemo(() => {
        if (preferencesLoading || branchesLoading) {
          return [];
        }

        return allOptions.map(formatBranch);
      }, [preferencesLoading, branchesLoading, preferences, allOptions.length]);
    };
  }
}
